import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { BracketsIcon } from '@/components/icons/BracketsIcon';
import { Lightbulb03Icon } from '@/components/icons/Lightbulb03Icon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { zIndexes } from '@/styles/zIndexes';

import { useVariableNames } from './hooks/useVariableNames';
import { COVER_SLIDE_TITLE_SUGGESTIONS } from './SlideEditor.titleSuggestions.constants';
import {
  SlideEditorToolbar,
  useSlideEditorToolbarContext,
} from './SlideEditor.toolbar';
import { SlideEditorToolbarDivider } from './SlideEditor.toolbarDivider';
import { useToolbarDropdown } from './SlideEditor.toolbarDropdown';

interface SlideEditorTitleToolbarProps {
  showSuggestions?: boolean;
  onSelectSuggestion: (suggestion: string) => void;
  onSelectVariable: (variableToken: string) => void;
}

export function SlideEditorTitleToolbar(props: SlideEditorTitleToolbarProps) {
  return (
    <SlideEditorToolbar>
      <SlideEditorTitleToolbarButtons {...props} />
    </SlideEditorToolbar>
  );
}

function SlideEditorTitleToolbarButtons({
  showSuggestions,
  onSelectSuggestion,
  onSelectVariable,
}: SlideEditorTitleToolbarProps) {
  const { activeDropdown, setActiveDropdown, toolbarRef } =
    useSlideEditorToolbarContext();
  const variableNames = useVariableNames();

  const suggestionsDropdown = useToolbarDropdown('suggestions');
  const variableDropdown = useToolbarDropdown('variable');

  return (
    <>
      {showSuggestions && (
        <>
          <div ref={suggestionsDropdown.containerRef}>
            <ButtonWithPopover
              label="See suggestions"
              size="xs"
              variant="transparent"
              popperVariant="menuBelow"
              startIcon={Lightbulb03Icon}
              highlightWhenOpen
              isOpen={activeDropdown === 'suggestions'}
              onClick={() =>
                setActiveDropdown((prev) =>
                  prev === 'suggestions' ? null : 'suggestions'
                )
              }
              onClose={() =>
                // Use functional update to avoid closing another dropdown that
                // just opened (which triggers onClose via the effect when isOpen goes false).
                setActiveDropdown((prev) =>
                  prev === 'suggestions' ? null : prev
                )
              }
              popperProps={{
                // Anchor to the toolbar (not the button) so the dropdown's left
                // edge aligns with the toolbar's left edge (first-item rule).
                anchorEl: toolbarRef.current,
                anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                // Negative vertical shifts the menu down, creating a 4px gap
                // between the toolbar and the menu.
                transformOrigin: { horizontal: 'left', vertical: -4 },
                sx: { zIndex: zIndexes.TOOLTIP },
              }}
            >
              {COVER_SLIDE_TITLE_SUGGESTIONS.map((suggestion) => (
                <MenuItem
                  key={suggestion}
                  label={suggestion}
                  onClick={() => onSelectSuggestion(suggestion)}
                />
              ))}
            </ButtonWithPopover>
          </div>
          <SlideEditorToolbarDivider />
        </>
      )}
      <div ref={variableDropdown.containerRef}>
        <ButtonWithPopover
          label="Add variable"
          size="xs"
          variant="transparent"
          popperVariant="menuBelow"
          startIcon={BracketsIcon}
          highlightWhenOpen
          isOpen={activeDropdown === 'variable'}
          onClick={() =>
            setActiveDropdown((prev) =>
              prev === 'variable' ? null : 'variable'
            )
          }
          onClose={() =>
            setActiveDropdown((prev) => (prev === 'variable' ? null : prev))
          }
          popperProps={{
            // When "Add variable" is the only item (no suggestions), it's the
            // first item → anchor to the toolbar so the dropdown left-aligns
            // with the toolbar edge. When suggestions are shown it's the second
            // item → anchor to the button instead.
            ...(!showSuggestions && { anchorEl: toolbarRef.current }),
            anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
            // Negative vertical shifts the menu down, creating a 4px gap
            // between the toolbar and the menu.
            transformOrigin: { horizontal: 'left', vertical: -4 },
            sx: { zIndex: zIndexes.TOOLTIP },
          }}
        >
          {variableNames.map((variableName) => (
            <MenuItem
              key={variableName}
              label={variableName}
              onClick={() => onSelectVariable(variableName)}
            />
          ))}
        </ButtonWithPopover>
      </div>
    </>
  );
}
